import React, { useState, useRef } from 'react';
import {
  Camera,
  ChevronDown,
  AlertTriangle,
  X,
  Plus,
  FileCheck,
  AlertCircle,
  Send,
  Upload,
  Save,
  Clock
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const AuditForm = () => {
  const [formData, setFormData] = useState({
    branch: '',
    auditor: '',
    date: new Date().toISOString().split('T')[0],
    startTime: '',
    sections: {
      FoodSafety: {
        score: 0,
        items: [
          { name: 'Temperature Control', score: 0, findings: [], photos: [] },
          { name: 'Storage Practices', score: 0, findings: [], photos: [] },
          { name: 'Personal Hygiene', score: 0, findings: [], photos: [] },
          { name: 'Cross Contamination', score: 0, findings: [], photos: [] }
        ]
      },
      customerService: {
        score: 0,
        items: [
          { name: 'Staff Courtesy', score: 0, findings: [], photos: [] },
          { name: 'Order Accuracy', score: 0, findings: [], photos: [] },
          { name: 'Wait Times', score: 0, findings: [], photos: [] }
        ]
      },
      cleanliness: {
        score: 0,
        items: [
          { name: 'Front of House', score: 0, findings: [], photos: [] },
          { name: 'Kitchen Area', score: 0, findings: [], photos: [] },
          { name: 'Washrooms', score: 0, findings: [], photos: [] },
          { name: 'External Areas', score: 0, findings: [], photos: [] }
        ]
      },
      operations: {
        score: 0,
        items: [
          { name: 'Documentation', score: 0, findings: [], photos: [] },
          { name: 'Staff Training', score: 0, findings: [], photos: [] },
          { name: 'Equipment Maintenance', score: 0, findings: [], photos: [] }
        ]
      }
    },
    notes: '',
    actionItems: []
  });

  const [expandedSections, setExpandedSections] = useState({
    FoodSafety: true,
    customerService: false,
    cleanliness: false,
    operations: false
  });

  const [currentFinding, setCurrentFinding] = useState('');
  const [currentActionItem, setCurrentActionItem] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [activeUpload, setActiveUpload] = useState({ sectionKey: null, itemIndex: null });
  const [showUnsavedChanges, setShowUnsavedChanges] = useState(false);
  const fileInputRef = useRef(null);

  const branches = [
    'Olaya Store',
    'Hafr Albatin',
    'Alahsa Mall',
    'Riyadh Park',
    'Red Sea Mall',
    'Hayat Mall'
  ];

  const navigate = useNavigate();

  const handleScoreChange = (section, itemIndex, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      newData.sections[section].items[itemIndex].score = parseInt(value);
      const sectionItems = newData.sections[section].items;
      const sectionScore = sectionItems.reduce((acc, item) => acc + item.score, 0) / sectionItems.length;
      newData.sections[section].score = Math.round(sectionScore);
      return newData;
    });
    setShowUnsavedChanges(true);
  };

  const handleAddFinding = (section, itemIndex) => {
    if (!currentFinding.trim()) return;
    
    const newFinding = {
      text: currentFinding,
      severity: 'medium',
      timestamp: new Date().toISOString()
    };
    
    setFormData(prev => {
      const newData = { ...prev };
      // Check if this finding already exists
      const existingFinding = newData.sections[section].items[itemIndex].findings
        .find(f => f.text === newFinding.text);
      
      if (!existingFinding) {
        newData.sections[section].items[itemIndex].findings.push(newFinding);
      }
      return newData;
    });
    setCurrentFinding('');
    setShowUnsavedChanges(true);
  };
  const handleAddActionItem = () => {
    if (!currentActionItem.trim()) return;
    setFormData(prev => ({
      ...prev,
      actionItems: [...prev.actionItems, {
        text: currentActionItem,
        status: 'pending',
        timestamp: new Date().toISOString()
      }]
    }));
    setCurrentActionItem('');
    setShowUnsavedChanges(true);
  };

  const handleFileUpload = (sectionKey, itemIndex) => {
    if (fileInputRef.current) {
      setActiveUpload({ sectionKey, itemIndex });
      
      // Check if the device supports the showPicker API
      if (typeof window !== 'undefined' && 
          window.showPicker && 
          /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        fileInputRef.current.showPicker()
          .catch(() => {
            // Fallback to click() if showPicker is not supported
            fileInputRef.current.click();
          });
      } else {
        fileInputRef.current.click();
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      
      reader.onload = (e) => {
        if (activeUpload.sectionKey && activeUpload.itemIndex !== null) {
          setFormData(prev => {
            const newData = { ...prev };
            // Check if the photo already exists
            const photoExists = newData.sections[activeUpload.sectionKey].items[activeUpload.itemIndex].photos
              .some(photo => photo.url === e.target.result);
            
            if (!photoExists) {
              newData.sections[activeUpload.sectionKey].items[activeUpload.itemIndex].photos.push({
                url: e.target.result,
                timestamp: new Date().toISOString()
              });
            }
            return newData;
          });
          setActiveUpload({ sectionKey: null, itemIndex: null });
          setShowUnsavedChanges(true);
        }
      };
      
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = (section, itemIndex, photoIndex) => {
    setFormData(prev => {
      const newData = { ...prev };
      newData.sections[section].items[itemIndex].photos.splice(photoIndex, 1);
      return newData;
    });
    setShowUnsavedChanges(true);
  };

  const handleRemoveFinding = (section, itemIndex, findingIndex) => {
    setFormData(prev => {
      const newData = { ...prev };
      newData.sections[section].items[itemIndex].findings.splice(findingIndex, 1);
      return newData;
    });
    setShowUnsavedChanges(true);
  };

  const handleSubmit = () => {
    if (!formData.branch || !formData.auditor || !formData.date || !formData.startTime) {
      alert('Please fill in all required fields');
      return;
    }
    
    console.log('Submitting audit:', formData);
    setShowConfirmation(true);
    setShowUnsavedChanges(false);
  };

  const calculateOverallScore = () => {
    const sections = Object.values(formData.sections);
    return Math.round(
      sections.reduce((acc, section) => acc + section.score, 0) / sections.length
    );
  };

  const renderScoreInput = (score) => {
    const getColor = () => {
      if (score >= 90) return 'text-emerald-600';
      if (score >= 80) return 'text-blue-600';
      if (score >= 70) return 'text-amber-600';
      return 'text-rose-600';
    };

    return (
      <div className={`text-2xl font-bold ${getColor()}`}>
        {score}%
      </div>
    );
  };

  const SectionHeader = ({ title, itemCount, score }) => (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full">
      <h2 className="text-lg font-semibold text-gray-900">
        {title.replace(/([A-Z])/g, ' $1').trim()}
      </h2>
      <div className="flex items-center gap-3 ml-auto">
        <div className="px-3 py-1 bg-indigo-100/50 text-indigo-700 rounded-full text-sm">
          {itemCount} items
        </div>
        {renderScoreInput(score)}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50/50">
      <div className="max-w-7xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6">
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl sm:rounded-2xl shadow p-4 sm:p-6 lg:p-8"
        >
          <div className="flex flex-col gap-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Store Audit</h1>
              <p className="text-gray-500">Complete the audit sections below</p>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Branch</label>
                <select
                  value={formData.branch}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, branch: e.target.value }));
                    setShowUnsavedChanges(true);
                  }}
                  className="w-full p-4 sm:p-3 text-base sm:text-sm border border-gray-200 rounded-xl 
                    focus:ring-2 focus:ring-indigo-500 bg-white/50 backdrop-blur-sm"
                >
                  <option value="">Select Branch</option>
                  {branches.map(branch => (
                    <option key={branch} value={branch}>{branch}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Auditor Name</label>
                <input
                  type="text"
                  value={formData.auditor}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, auditor: e.target.value }));
                    setShowUnsavedChanges(true);
                  }}
                  className="w-full p-4 sm:p-3 text-base sm:text-sm border border-gray-200 rounded-xl 
                    focus:ring-2 focus:ring-indigo-500 bg-white/50 backdrop-blur-sm"
                  placeholder="Enter your name"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, date: e.target.value }));
                    setShowUnsavedChanges(true);
                  }}
                  className="w-full p-4 sm:p-3 text-base sm:text-sm border border-gray-200 rounded-xl 
                    focus:ring-2 focus:ring-indigo-500 bg-white/50 backdrop-blur-sm"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Start Time</label>
                <input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, startTime: e.target.value }));
                    setShowUnsavedChanges(true);
                  }}
                  className="w-full p-4 sm:p-3 text-base sm:text-sm border border-gray-200 rounded-xl 
                    focus:ring-2 focus:ring-indigo-500 bg-white/50 backdrop-blur-sm"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Audit Sections */}
        {Object.entries(formData.sections).map(([sectionKey, section], index) => (
          <motion.div
            key={sectionKey}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden"
          >
            <button
              onClick={() => setExpandedSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }))}
              className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-gray-50/50 
                transition-colors rounded-t-xl active:bg-gray-100/50"
            >
              <SectionHeader 
                title={sectionKey} 
                itemCount={section.items.length} 
                score={section.score} 
              />
              <ChevronDown 
                className={`w-6 h-6 text-gray-400 transition-transform duration-200
                  ${expandedSections[sectionKey] ? 'rotate-180' : ''}`}
              />
            </button>

            <AnimatePresence>
              {expandedSections[sectionKey] && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="px-8 pb-8"
                >
                  <div className="space-y-8">
                    {section.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="border-t pt-6">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
                          <h3 className="text-lg font-medium text-gray-900">{item.name}</h3>
                          <div className="flex items-center gap-3 w-full sm:w-auto">
                            <span className="text-sm font-medium text-gray-500">Score</span>
                            <select
                              value={item.score}
                              onChange={(e) => handleScoreChange(sectionKey, itemIndex, e.target.value)}
                              className="p-2 border border-gray-200 rounded-lg w-24 text-lg 
                                font-medium focus:ring-2 focus:ring-indigo-500 bg-white/50"
                            >
                              {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(score => (
                                <option key={score} value={score}>{score}%</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Findings */}
                        <div className="mb-6 space-y-3">
                          <div className="flex flex-col gap-3">
                            <input
                              type="text"
                              value={currentFinding}
                              onChange={(e) => setCurrentFinding(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleAddFinding(sectionKey, itemIndex)}
                              className="w-full p-4 text-base border border-gray-200 rounded-xl 
                                focus:ring-2 focus:ring-indigo-500 bg-white"
                              placeholder="Add observation or finding..."
                            />
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => handleAddFinding(sectionKey, itemIndex)}
                              className="w-full sm:w-auto p-4 bg-indigo-600 text-white rounded-xl 
                                hover:bg-indigo-700 transition-all flex items-center justify-center gap-2"
                            >
                              <Plus className="w-5 h-5" />
                              Add Finding
                            </motion.button>
                          </div>

                          <div className="space-y-2 mt-4">
                            {item.findings.map((finding, findingIndex) => (
                              <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                key={findingIndex}
                                className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl"
                              >
                                <AlertCircle className="w-5 h-5 text-blue-700 mt-0.5 flex-shrink-0" />
                                <div className="flex-1 text-gray-600 break-words">{finding.text}</div>
                                <button
                                  onClick={() => handleRemoveFinding(sectionKey, itemIndex, findingIndex)}
                                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                  <X className="w-4 h-4 text-gray-500" />
                                </button>
                              </motion.div>
                            ))}
                          </div>
                        </div>

                        {/* Photos */}
                        <div className="mt-6">
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                            {item.photos.map((photo, photoIndex) => (
                              <motion.div
                                key={photoIndex}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="relative aspect-square"
                              >
                                <img
                                  src={photo.url}
                                  alt={`Photo ${photoIndex + 1}`}
                                  className="w-full h-full object-cover rounded-xl"
                                />
                                <button
                                  onClick={() => handleRemovePhoto(sectionKey, itemIndex, photoIndex)}
                                  className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-lg"
                                >
                                  <X className="w-4 h-4 text-gray-500" />
                                </button>
                              </motion.div>
                            ))}
                            <motion.button
                              onClick={() => handleFileUpload(sectionKey, itemIndex)}
                              className="aspect-square border-2 border-dashed border-gray-200 rounded-xl 
                                flex flex-col items-center justify-center gap-2 hover:border-indigo-400 
                                hover:bg-indigo-50 transition-all"
                            >
                              <div className="p-3 bg-gray-100 rounded-full">
                                <Camera className="w-6 h-6 text-gray-500" />
                              </div>
                              <span className="text-sm font-medium text-gray-500">Add Photo</span>
                            </motion.button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}

        {/* Action Items */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-4 sm:p-8"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Action Items</h2>
          <div className="space-y-4">
            {/* Action Item Input */}
            <div className="flex flex-col gap-3">
              <input
                type="text"
                value={currentActionItem}
                onChange={(e) => setCurrentActionItem(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddActionItem();
                  }
                }}
                className="w-full p-4 text-base border border-gray-200 rounded-xl 
                  focus:ring-2 focus:ring-indigo-500 bg-white/50"
                placeholder="Add action item..."
              />
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleAddActionItem}
                className="w-full sm:w-auto p-4 bg-indigo-600 text-white rounded-xl 
                  hover:bg-indigo-700 transition-all flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add Action Item
              </motion.button>
            </div>

            {/* Action Items List */}
            <div className="space-y-3 mt-6">
              {formData.actionItems.map((item, index) => (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  key={index}
                  className="flex flex-col sm:flex-row items-start gap-3 p-4 bg-gray-50 
                    rounded-xl relative group"
                >
                  <div className="flex items-center gap-3 w-full">
                    <div className="bg-indigo-50 p-2 rounded-lg flex-shrink-0">
                      <FileCheck className="w-4 h-4 text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 break-words">{item.text}</p>
                      <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                        <Clock className="w-3.5 h-3.5" />
                        <span>{new Date(item.timestamp).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit'
                        })}</span>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          actionItems: prev.actionItems.filter((_, i) => i !== index)
                        }));
                        setShowUnsavedChanges(true);
                      }}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-red-400" />
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Notes Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg"
        >
          <div className="p-8 space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-gray-50 p-2 rounded-lg">
                <FileCheck className="w-5 h-5 text-gray-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Additional Notes</h2>
            </div>
            <textarea
              value={formData.notes}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, notes: e.target.value }));
                setShowUnsavedChanges(true);
              }}
              className="w-full h-40 p-5 border border-gray-200 rounded-xl resize-none 
                focus:ring-2 focus:ring-indigo-500 bg-white/50 text-gray-700
                placeholder:text-gray-400"
              placeholder="Enter any additional notes or observations..."
            />
          </div>
        </motion.div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />

        {/* Confirmation Modal */}
        <AnimatePresence>
          {showConfirmation && (
            <motion.div
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]"
            >
              <motion.div
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 100, opacity: 0 }}
                className="bg-white rounded-2xl max-w-lg w-full mx-auto p-5 sm:p-6 lg:p-8 
                  mb-20 sm:mb-0"
              >
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileCheck className="w-8 h-8 text-emerald-500" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                    Audit Submitted Successfully
                  </h3>
                  <p className="text-gray-500">
                    Your audit has been submitted and is ready for review
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
                  <div>
                    <span className="text-sm text-gray-500">Branch</span>
                    <p className="font-medium text-gray-900 mt-1">{formData.branch}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Date</span>
                    <p className="font-medium text-gray-900 mt-1">{formData.date}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Auditor</span>
                    <p className="font-medium text-gray-900 mt-1">{formData.auditor}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Overall Score</span>
                    <p className="font-medium text-gray-900 mt-1">{calculateOverallScore()}%</p>
                  </div>
                </div>

                <div className="flex justify-end">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate('/audit')}
                    className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 
                      transition-all shadow-lg hover:shadow-xl font-medium"
                  >
                    Done
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Unsaved Changes Alert - Now shows as a small pill notification */}
        <AnimatePresence>
          {showUnsavedChanges && (
            <motion.div
              className="fixed bottom-28 sm:bottom-24 left-1/2 -translate-x-1/2 px-4 py-3 
                bg-amber-500 text-white rounded-full shadow-lg flex items-center 
                gap-2 text-sm font-medium z-40"
            >
              <AlertTriangle className="w-4 h-4" />
              Unsaved changes
            </motion.div>
          )}
        </AnimatePresence>

        {/* Add this new sticky footer component at the bottom of the form (after all sections, before closing div) */}
        <motion.div 
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          className="fixed bottom-0 left-0 right-0 z-50"
        >
          {/* Progress Bar */}
          <div className="bg-gray-100 h-1">
            <div 
              className="bg-indigo-600 h-1 transition-all duration-300" 
              style={{ 
                width: `${Math.round(
                  (Object.values(formData.sections)
                    .reduce((acc, section) => acc + section.score, 0) / 
                    (Object.values(formData.sections).length * 100)) * 100
                )}%` 
              }} 
            />
          </div>

          {/* Footer Content */}
          <div className="bg-white border-t border-gray-200 shadow-lg px-4 py-3">
            <div className="max-w-7xl mx-auto flex flex-col gap-3">
              {/* Stats Row - Scrollable on mobile */}
              <div className="flex items-center gap-4 overflow-x-auto pb-2 hide-scrollbar">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-indigo-600" />
                  <span className="text-sm text-gray-600">Score: </span>
                  <span className="font-semibold">{calculateOverallScore()}%</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-emerald-600" />
                  <span className="text-sm text-gray-600">Findings: </span>
                  <span className="font-semibold">
                    {Object.values(formData.sections).reduce((acc, section) => 
                      acc + section.items.reduce((itemAcc, item) => 
                        itemAcc + item.findings.length, 0), 0)}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-blue-600" />
                  <span className="text-sm text-gray-600">Photos: </span>
                  <span className="font-semibold">
                    {Object.values(formData.sections).reduce((acc, section) => 
                      acc + section.items.reduce((itemAcc, item) => 
                        itemAcc + item.photos.length, 0), 0)}
                  </span>
                </div>
              </div>

              {/* Submit Button */}
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={handleSubmit}
                disabled={!formData.branch || !formData.auditor || !formData.date || !formData.startTime}
                className={`w-full py-3.5 rounded-xl flex items-center justify-center gap-2 
                  font-medium transition-all ${
                  (!formData.branch || !formData.auditor || !formData.date || !formData.startTime)
                    ? 'bg-gray-100 text-gray-400'
                    : 'bg-indigo-600 text-white shadow-lg active:shadow-sm'
                }`}
              >
                {(!formData.branch || !formData.auditor || !formData.date || !formData.startTime) ? (
                  <>
                    <AlertCircle className="w-5 h-5" />
                    Complete Required Fields
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Audit
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Add padding at the bottom of the main container to account for the fixed footer */}
        <div className="pb-32" /> {/* Add this before the closing main div */}

        {/* Add this CSS to hide scrollbar but keep functionality */}
        <style jsx>{`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </div>
    </div>
  );
};

export default AuditForm;